import React, { lazy, useCallback } from "react";
import { useEffect } from "react";
import { useState } from "react";

import cx from "classnames";
import SSRSuspense from "../../components/SSRSuspense/SSRSuspense";
import { usePageManager } from "../../context";
import { useRegisterSection } from "../../hooks/useRegisterSection";
import { Colours } from "../../utils/colours";

const SocialSelectionSharing =
	process.env.GATSBY_IS_IE === "true"
		? lazy(
				() =>
					import(
						"../../components/common/SocialSelectionSharing/SocialSelectionSharing.ie"
					),
			)
		: lazy(
				() =>
					import(
						"../../components/common/SocialSelectionSharing/SocialSelectionSharing"
					),
			);

const SNACKABLE_SECTIONS = [
	"ParagraphContentSection",
	"ParagraphChecklist",
	"SectionSteppedProcess",
	"ParagraphTextArea",
	"ParagraphQuote",
	"ParagraphCAQ",
	"ParagraphFAQ",
];

export interface WrappedSectionProps {
	children?: React.ReactNode;
	sectionName: string;
	sectionColour?: string;
	sectionTopImage?: DogsTrust.MediaImageProps;
	sectionBottomImage?: DogsTrust.MediaImageProps;
	customBorder?: string;
	isHomePage?: boolean;
	isChild?: boolean;
	className?: string;
	ariaHidden?: boolean;
	updateParagraphContentSectionTheme?: (theme: string) => void;
}

const useHandleShareSelected = (id: string, sectionName: string) => {
	const [selection, setSelection] = useState<string>(null);
	const [showPopup, setShowPopup] = useState(false);
	const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

	// biome-ignore lint/correctness/useExhaustiveDependencies: This effect needs to run when the id is finalised
	useEffect(() => {
		const handleEmptySelection = () => {
			const selectedText = window.getSelection().toString().trim();

			if (!selectedText) {
				setShowPopup(false);
				setSelection(null);
			}
		};

		if (SNACKABLE_SECTIONS.includes(sectionName)) {
			document.addEventListener("mousedown", handleSelectStart);
			document.addEventListener("mouseup", handleSelectEnd);
			document.addEventListener("selectionchange", handleEmptySelection);
		}
		return () => {
			if (SNACKABLE_SECTIONS.includes(sectionName)) {
				document.removeEventListener("mousedown", handleSelectStart);
				document.removeEventListener("mouseup", handleSelectEnd);
				document.removeEventListener("selectionchange", handleEmptySelection);
			}
		};
	}, [id, sectionName]);

	/**
	 * This function handles the select start event and limits the user to selecting content within the section
	 * @param e - The mouse event
	 * @returns {void}
	 */
	const handleSelectStart = useCallback(
		(e: MouseEvent) => {
			if ((e.target as Element).closest("section")?.id !== id) {
				return;
			}
			const sections = document.querySelectorAll("section");
			for (const section of Array.from(sections)) {
				if (section.id !== id) {
					section.style.userSelect = "none";
				} else {
					section.style.userSelect = "auto";
				}
			}
		},
		[id],
	);

	/**
	 * This function handles the select end event and displays the share popup if text is selected
	 * @returns {void}
	 */
	/**
	 * Handles the selection end event.
	 * If the selected text spans across multiple sections, the selection is cleared and the popup is hidden.
	 * If there is selected text, the popup position is set and the popup is shown.
	 * If there is no selected text, the popup is hidden.
	 */
	const handleSelectEnd = useCallback(() => {
		const selectedText = window.getSelection().toString().trim();
		const selection = window.getSelection();

		if (selection.rangeCount > 0) {
			const range = selection.getRangeAt(0);
			const startElement = range.startContainer.parentElement;
			const endElement = range.endContainer.parentElement;

			// Check that the selection is within this section
			const section = document.getElementById(id);
			if (!section.contains(startElement) || !section.contains(endElement)) {
				setShowPopup(false);
				setSelection(null);
				setPopupPosition(null);
				return;
			}

			if (startElement.closest("section") !== endElement.closest("section")) {
				selection.removeAllRanges();
				setShowPopup(false);
				return;
			}
			if (selectedText) {
				const activeSelection = document.getSelection();

				// Check if the selection is within the component's DOM element
				const componentRect = section.getBoundingClientRect();

				// Calculate position relative to the component's bounding box
				const rect = activeSelection.getRangeAt(0).getBoundingClientRect();

				setPopupPosition({
					left: rect.left - componentRect.left,
					top: rect.top - componentRect.top + rect.height + 20,
				});

				// const { top, left } = range.getBoundingClientRect();
				// setPopupPosition({ top, left });
				// Sets the popup position to the last position of the mouse
				//setPopupPosition({ top: e.clientY + 30, left: e.clientX - 45 });
				setSelection(encodeURIComponent(selectedText));
				setShowPopup(true);
			} else {
				setShowPopup(false);
				setSelection(null);
			}
			const sections = document.querySelectorAll("section");
			for (const section of Array.from(sections)) {
				section.style.userSelect = "auto";
			}
		} else {
			setShowPopup(false);
			setSelection(null);
		}
	}, [id]);

	return { selection, popupPosition, showPopup };
};

export const WrappedSection: React.FC<WrappedSectionProps> = ({
	sectionName,
	sectionColour = Colours.WHITE,
	sectionTopImage,
	sectionBottomImage,
	customBorder,
	isHomePage,
	isChild,
	children,
	className,
	ariaHidden,
	updateParagraphContentSectionTheme,
}) => {
	const [sectionId, setSectionId] = useState<string | undefined>(undefined);

	const {
		getOuterSectionClassName,
		getInnerSectionClassName,
		getColourBandClassName,
		getSectionTopBorder,
		getSectionBottomBorder,
		registerSection,

		getParagraphContentSectionTheme,
	} = usePageManager();

	useRegisterSection((sectionId: string) => {
		setSectionId(sectionId);
		registerSection(
			sectionName,
			sectionId,
			sectionColour,
			sectionTopImage,
			sectionBottomImage,
			customBorder,
			isHomePage,
			isChild,
		);
	});

	useEffect(() => {
		if (sectionId && updateParagraphContentSectionTheme)
			updateParagraphContentSectionTheme(
				getParagraphContentSectionTheme(sectionId),
			);
	}, [
		sectionId,
		updateParagraphContentSectionTheme,
		getParagraphContentSectionTheme,
	]);

	const id = `${sectionName}=${sectionId}`;
	const { selection, popupPosition, showPopup } = useHandleShareSelected(
		id,
		sectionName,
	);
	const currentPageUrl =
		typeof window !== "undefined" ? window.location.href : "";

	return (
		<>
			{getSectionTopBorder(sectionId, isChild)}
			<section
				id={id}
				className={cx(
					getOuterSectionClassName(sectionId, isHomePage, isChild),
					className,
				)}
				aria-hidden={ariaHidden}
			>
				<div
					className={getInnerSectionClassName(sectionId, isHomePage, isChild)}
				>
					{children}
				</div>
				<div className={getColourBandClassName(sectionId)} />
				{showPopup && selection?.length > 0 && (
					<SSRSuspense>
						<SocialSelectionSharing
							shareBodyText={selection}
							currentPageUrl={currentPageUrl}
							positionX={popupPosition.left}
							positionY={popupPosition.top}
						/>
					</SSRSuspense>
				)}
			</section>

			{getSectionBottomBorder(sectionId, isChild)}
		</>
	);
};

export const UnwrappedWrappedSection: React.FC<WrappedSectionProps> = ({
	children,
}) => {
	return <>{children}</>;
};
export default WrappedSection;
